import { useEffect } from "react";
import { toConfiguredAbsoluteUrl } from "../config/api";
import aboutHero from "../assets/about-hero.jpg";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";
const DEFAULT_DOCUMENT_DESCRIPTION =
  "Steel, hardware, fasteners, roofing, paint and industrial products for contractors across Malaysia. Request a quote today.";
const ABOUT_JSON_LD_ID = "about-webpage-jsonld";
const BREADCRUMB_JSON_LD_ID = "about-breadcrumb-jsonld";

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.aboutSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.aboutSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.aboutSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.aboutSeo = "true";
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function clearAboutSeo() {
  document.head.querySelectorAll("[data-about-seo='true']").forEach((el) => el.remove());
  upsertJsonLd(ABOUT_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

export function aboutPageSeo(contact) {
  const siteName = cleanText(contact?.siteName);
  if (!siteName) return null;
  return {
    siteName,
    title: `About ${siteName} | Industrial Supply Partner Malaysia`,
    description: `Learn about ${siteName}, an industrial supply partner supporting contractors with steel, hardware, product sourcing and delivery across Malaysia.`,
    imageAlt: `About ${siteName}, an industrial supply partner in Malaysia`,
  };
}

function applyNeutralMetadata(canonicalUrl) {
  document.title = DEFAULT_DOCUMENT_TITLE;
  upsertMeta("name", "description", DEFAULT_DOCUMENT_DESCRIPTION);
  upsertLink("canonical", canonicalUrl);
  upsertMeta("name", "robots", "");
  upsertJsonLd(ABOUT_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

function AboutSeo({ contact, contactStatus = "loading" }) {
  useEffect(() => {
    const canonicalUrl = toConfiguredAbsoluteUrl("/about");
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const websiteId = `${String(homeUrl || "/").replace(/\/$/, "")}/#website`;
    const organizationId = `${String(homeUrl || "/").replace(/\/$/, "")}/#organization`;

    if (contactStatus === "loading") {
      applyNeutralMetadata(canonicalUrl);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearAboutSeo();
      };
    }

    const seo = aboutPageSeo(contact);
    if (!seo) {
      applyNeutralMetadata(canonicalUrl);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearAboutSeo();
      };
    }

    const imageUrl = toConfiguredAbsoluteUrl(aboutHero);

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertLink("canonical", canonicalUrl);
    upsertMeta("name", "robots", "");

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? seo.imageAlt : "");
    upsertMeta("property", "og:site_name", seo.siteName);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? seo.imageAlt : "");

    upsertJsonLd(
      ABOUT_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "@id": canonicalUrl ? `${canonicalUrl}#webpage` : undefined,
        url: canonicalUrl || undefined,
        name: seo.title,
        description: seo.description,
        isPartOf: { "@id": websiteId },
        about: { "@id": organizationId },
        breadcrumb: canonicalUrl ? { "@id": `${canonicalUrl}#breadcrumb` } : undefined,
      })
    );

    upsertJsonLd(
      BREADCRUMB_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": canonicalUrl ? `${canonicalUrl}#breadcrumb` : undefined,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "About Us", item: canonicalUrl },
        ],
      })
    );

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      clearAboutSeo();
    };
  }, [contact, contactStatus]);

  return null;
}

export default AboutSeo;
